<script setup lang="ts">
import { useCrud } from "~/composables/use-crud";
import type { Workspace } from "@artech-global/types";
import { deburr } from "lodash-es";

defineOptions({ name: "WorkspaceFormModal" });

const emit = defineEmits<{ (e: "saved"): void }>();

const { t } = useI18n();
const toast = useToast();

const isOpen = ref(false);

const {
  formModel,
  formErrors,
  errorMsg,
  isCreate,
  saveLoading,
  save,
  load,
  reset,
} = useCrud<Workspace>({
  endpoint: "/workspaces",
  defaultFormModel: () =>
    ({
      id: "",
      name: "",
      code: "",
      desc: "",
      image: "",
      ownerName: "",
      ownerEmail: "",
      sendEmail: true,
    }) as unknown as Workspace,
});

watch(
  () => (formModel as any).code,
  (val) => {
    if (!val) return;
    const normalized = deburr(String(val))
      .trim()
      .replace(/\s+/g, "")
      .toUpperCase();
    if (normalized !== val) {
      (formModel as any).code = normalized;
    }
  },
);

async function open(id?: string) {
  reset();
  if (id) {
    try {
      await load(id);
    } catch {
      if (errorMsg.value) toast.error({ title: errorMsg.value });
      return;
    }
  }
  isOpen.value = true;
}

async function submit() {
  const isCreating = isCreate.value;
  try {
    await save();
    isOpen.value = false;
    toast.success({
      title: t(isCreating ? "message.createSuccess" : "message.updateSuccess"),
    });
    emit("saved");
  } catch {
  }
}

defineExpose({ open });
</script>

<template>
  <Modal
    v-model:open="isOpen"
    :title="isCreate ? t('workspace.add') : t('workspace.edit')"
    size="md"
    @close="isOpen = false"
  >
    <div class="flex flex-col gap-medium">
      <FormField
        v-model="(formModel as any).name"
        :label="t('workspace.nameLabel')"
        :error="formErrors.name"
        required
      />

      <FormField
        v-model="(formModel as any).code"
        :label="t('workspace.codeLabel')"
        :help-text="t('workspace.codeHelp')"
        :error="formErrors.code"
        required
      />

      <FormTextarea
        v-model="(formModel as any).desc"
        :label="t('workspace.descLabel')"
        :error="formErrors.desc"
      />

      <template v-if="isCreate">
        <FormField
          v-model="(formModel as any).ownerName"
          :label="t('workspace.ownerNameLabel')"
          :error="formErrors.ownerName"
        />

        <FormField
          v-model="(formModel as any).ownerEmail"
          type="email"
          :label="t('workspace.ownerEmailLabel')"
          :error="formErrors.ownerEmail"
        />

        <FormSwitch
          v-model="(formModel as any).sendEmail"
          :label="t('workspace.sendEmailLabel')"
        />
      </template>

      <InlineMessage v-if="errorMsg" variant="negative">{{ errorMsg }}</InlineMessage>
    </div>

    <template #actions>
      <Button variant="tertiary" size="medium" @click="isOpen = false">
        {{ t("action.cancel") }}
      </Button>
      <Button
        variant="primary"
        size="medium"
        :disabled="saveLoading"
        @click="submit"
      >
        {{ t("action.save") }}
      </Button>
    </template>
  </Modal>
</template>
